import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  GetApp as DownloadIcon,
  
} from '@mui/icons-material';

// Define language options
interface Language {
  name: string;
  icon: string;
  file: string;
  description: string;
  tags: string[];
}

interface ExampleEntry {
  language: string;
  file: string;
  alternateFile?: string;
  title: string;
  description: string;
  icon: string;
  tags: string[];
}

const examples: ExampleEntry[] = [
  {
    language: 'Python',
    icon: '🐍',
    file: 'vendor_api_integration_example.py',
    alternateFile: 'lis_integration_example.py',
    title: 'Python Example',
    description: 'Demonstrates API integration with the LIS vendor API using Python. Includes examples of authentication, data submission, and error handling.',
    tags: ['requests', 'json', 'hmac']
  },
  {
    language: 'Java',
    icon: '☕',
    file: 'vendor_api_integration_example_java.java',
    title: 'Java Example',
    description: 'Shows how to integrate with our API using Java. This example provides a complete implementation of all key API endpoints.',
    tags: ['HttpClient', 'Java 11+', 'JSON']
  },
  {
    language: 'Go',
    icon: '🔵',
    file: 'vendor_api_integration_example.go',
    title: 'Go Example',
    description: 'Complete Go implementation for API integration, featuring concurrent request handling and robust error management.',
    tags: ['net/http', 'json', 'error handling']
  },
  {
    language: 'Rust',
    icon: '🦀',
    file: 'vendor_api_integration_example.rs',
    title: 'Rust Example',
    description: 'Secure and efficient Rust implementation of our API client with comprehensive error handling and type safety.',
    tags: ['reqwest', 'tokio', 'serde']
  },
  {
    language: 'C#',
    icon: '🔷',
    file: 'VendorApiIntegrationExample.cs',
    title: 'C# Example',
    description: 'Full .NET implementation showing integration with our API services using modern C# features and async patterns.',
    tags: ['HttpClient', '.NET', 'async/await']
  },
];

const ApiExamples: React.FC = () => {
  const [tabValue, setTabValue] = useState<number>(0);
  const navigate = useNavigate();

  // Create tab labels array from examples
  const tabLabels = ['All', ...examples.map(ex => ex.language)];

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleBack = () => {
    navigate('/lis-portal/documentation');
  };

  // Handle downloading of code examples with fallback to alternate filename
  const handleDownload = async (example: ExampleEntry) => {
    try {
      const baseUrl = process.env.PUBLIC_URL || '/vendor-portal';
      
      // Try the primary filename first
      let downloadUrl = `${baseUrl}/examples/${example.file}`;
      let response = await fetch(downloadUrl);
      
      // If the primary file doesn't exist and there's an alternate, try that
      if (!response.ok && example.alternateFile) {
        downloadUrl = `${baseUrl}/examples/${example.alternateFile}`;
        response = await fetch(downloadUrl);
        
        if (!response.ok) {
          throw new Error(`Neither example file could be found`);
        }
      } else if (!response.ok) {
        throw new Error(`Example file could not be found`);
      }
      
      console.log(`Downloading ${example.language} example from: ${downloadUrl}`);
      
      // Get the blob from the response
      const blob = await response.blob();
      
      // Extract filename from the URL for the download
      const filename = downloadUrl.split('/').pop() || `${example.language.toLowerCase()}_example`;
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      
      // Trigger the download
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error(`Error downloading ${example.language} example:`, error);
      alert(`Error downloading ${example.language} example: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleViewDocs = () => {
    navigate('/lis-portal/integration-guide');
  };

  // Filter examples based on selected language tab
  const filteredExamples = tabValue === 0 
    ? examples 
    : examples.filter(ex => ex.language.toLowerCase() === tabLabels[tabValue].toLowerCase());

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={handleBack}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h5" component="h2">
          API Integration Examples
        </Typography>
      </Box>
      
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="programming language examples tabs"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="All Languages" />
          {examples.map((ex, index) => (
            <Tab key={index} label={ex.language} icon={<span>{ex.icon}</span>} iconPosition="start" />
          ))}
        </Tabs>
      </Paper>
      
      <Grid container spacing={3}>
        {filteredExamples.map((example, index) => (
          <Grid item xs={12} md={tabValue === 0 ? 6 : 12} key={index}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" component="h3" sx={{ mr: 1 }}>
                    {example.icon} {example.title}
                  </Typography>
                </Box>
                <Typography sx={{ mb: 2 }}>
                  {example.description}
                </Typography>
                <Box sx={{ mb: 2 }}>
                  {example.tags.map((tag, i) => (
                    <Chip 
                      key={i} 
                      label={tag} 
                      size="small" 
                      sx={{ mr: 0.5, mb: 0.5 }} 
                    />
                  ))}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Filename: {example.file}
                  {example.alternateFile && ` (or ${example.alternateFile})`}
                </Typography>
              </CardContent>
              <CardActions>
                <Button 
                  size="small" 
                  color="primary" 
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownload(example)}
                >
                  Download {example.language} Example
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ApiExamples; 